import * as turf from '@turf/turf';
import { GeoJSONSource, LngLat, Map as MaplibreMap, MapMouseEvent } from 'maplibre-gl';
import { v4 } from 'uuid';
import assert from '../../../utils/assert';
import { DrawingObjectPolygonGeometry } from '../types';
import { FeDrawMode } from './draw-mode';

export class FePolygonEditMode extends FeDrawMode {
    constructor(
        private readonly map: MaplibreMap,
        private readonly source: GeoJSONSource
    ) {
        super();
    }

    private dragging = false;
    private dragVertexId: number | null = null;
    private dragLastPoint: LngLat | null = null;
    private feature: GeoJSON.Feature<GeoJSON.Polygon> | null = null;
    private points: GeoJSON.Feature<GeoJSON.Point, { type: string; vertexId: number; active: boolean }>[] = [];
    private midPoints: GeoJSON.Feature<GeoJSON.Point, { type: string; vertexId: number }>[] = [];

    override cancel(): void {
        this.source.setData(turf.featureCollection([]));
    }

    override onMouseMove(e: MapMouseEvent): void {
        if (!this.dragging) {
            return;
        }

        assert(this.dragLastPoint);
        assert(this.feature);
        const deltaLng = e.lngLat.lng - this.dragLastPoint.lng;
        const deltaLat = e.lngLat.lat - this.dragLastPoint.lat;
        this.dragLastPoint = e.lngLat;

        if (this.dragVertexId != null) {
            if (Math.abs(this.points[this.dragVertexId].geometry.coordinates[1] + deltaLat) > 85) {
                return;
            }

            this.feature.geometry.coordinates[0][this.dragVertexId][0] += deltaLng;
            this.feature.geometry.coordinates[0][this.dragVertexId][1] += deltaLat;

            if (this.dragVertexId === 0) {
                const coords = this.feature.geometry.coordinates[0];
                coords[coords.length - 1][0] += deltaLng;
                coords[coords.length - 1][1] += deltaLat;
            }

            this.points[this.dragVertexId].geometry.coordinates[0] += deltaLng;
            this.points[this.dragVertexId].geometry.coordinates[1] += deltaLat;

            const rightVertex = arrayWrap(this.dragVertexId, +1, this.points);
            const leftMidPoint = arrayWrap(this.dragVertexId, -1, this.points);

            this.midPoints[this.dragVertexId].geometry = midpointGeoJson(this.points[this.dragVertexId], this.points[rightVertex]).geometry;
            this.midPoints[leftMidPoint].geometry = midpointGeoJson(this.points[this.dragVertexId], this.points[leftMidPoint]).geometry;

            this.updateFeature('pointDrag');
        } else {
            if (this.points.some((x) => Math.abs(x.geometry.coordinates[1] + deltaLat) > 85)) {
                return;
            }
            for (const cord of this.feature.geometry.coordinates[0]) {
                cord[0] += deltaLng;
                cord[1] += deltaLat;
            }
            for (const cord of this.points) {
                cord.geometry.coordinates[0] += deltaLng;
                cord.geometry.coordinates[1] += deltaLat;
            }

            for (const midPoint of this.midPoints) {
                midPoint.geometry.coordinates[0] += deltaLng;
                midPoint.geometry.coordinates[1] += deltaLat;
            }
            this.updateFeature('move');
        }
    }

    override onMouseDown(e: MapMouseEvent): void {
        if (this.dragVertexId != null) {
            this.setActive(this.dragVertexId, false);
        }

        const results = this.map.queryRenderedFeatures(e.point, {
            layers: ['fe-drawing-fill', 'fe-drawing-points-outline', 'fe-drawing-midpoints']
        });
        if (results.length === 0) {
            return;
        }

        this.dragging = true;
        this.dragLastPoint = e.lngLat;
        e.preventDefault();

        const midPoint = results.find((x) => x.properties.type === 'midpoint');
        if (midPoint) {
            // Convert mid point into vertex
            this.convertMidPointIntoVertex(midPoint.properties.vertexId);
            this.dragVertexId = arrayWrap(midPoint.properties.vertexId, +1, this.midPoints);
        } else {
            const dragVertex = results.find((x) => x.layer.id === 'fe-drawing-points-outline');
            this.dragVertexId = dragVertex?.properties?.vertexId ?? null;
        }

        if (this.dragVertexId != null) {
            this.setActive(this.dragVertexId, true);
        }
    }

    override onKeyDown(e: KeyboardEvent): void {
        if (e.key !== 'Delete' && e.key !== 'Backspace') {
            return;
        }

        if (this.points.length <= 3) {
            return;
        }

        if (this.dragVertexId != null) {
            assert(this.feature);
            const removedPoint = this.points[this.dragVertexId];
            const removedMidPoint = this.midPoints[this.dragVertexId];

            this.points.splice(this.dragVertexId, 1);
            this.midPoints.splice(this.dragVertexId, 1);

            for (let i = 0; i < this.points.length; i++) {
                this.points[i].properties.vertexId = i;
                this.midPoints[i].properties.vertexId = i;
            }

            for (let i = 0; i < this.midPoints.length - 1; i++) {
                this.midPoints[i].geometry.coordinates = midpointGeoJson(this.points[i], this.points[i + 1]).geometry.coordinates;
            }
            this.feature.geometry.coordinates[0].splice(this.dragVertexId, 1);

            // If the first point was removed, repeat the first point at the end of the polygon
            if (this.dragVertexId === 0) {
                this.feature.geometry.coordinates[0][this.feature.geometry.coordinates[0].length - 1] = [
                    ...this.feature.geometry.coordinates[0][0]
                ];
            }

            this.source.updateData({
                remove: [removedPoint.id as string, removedMidPoint.id as string],
                update: [
                    ...this.points.map((x) => ({
                        id: x.id as string,
                        addOrUpdateProperties: [
                            {
                                key: 'vertexId',
                                value: x.properties.vertexId
                            }
                        ]
                    })),
                    ...this.midPoints.map((x) => ({
                        id: x.id as string,
                        newGeometry: x.geometry,
                        addOrUpdateProperties: [
                            {
                                key: 'vertexId',
                                value: x.properties.vertexId
                            }
                        ]
                    })),
                    {
                        id: this.feature.id as string,
                        newGeometry: this.feature.geometry
                    }
                ]
            });

            this.dragVertexId = null;
            this.dragging = false;
        }
    }

    override onMouseUp(e: MapMouseEvent): void {
        this.dragging = false;
    }

    override edit(geometry: DrawingObjectPolygonGeometry): void {
        this.feature = {
            ...turf.polygon([geometry.polygonCoordinates.map((x) => [...x])]),
            id: v4()
        };

        this.points = this.feature.geometry.coordinates[0].slice(0, this.feature.geometry.coordinates.length - 2).map((x, i) => ({
            type: 'Feature',
            id: v4(),
            geometry: {
                type: 'Point',
                coordinates: [...x]
            },
            properties: {
                type: 'vertex',
                vertexId: i,
                active: false
            }
        }));

        this.midPoints = [];
        const coords = geometry.polygonCoordinates;
        for (let i = 0; i < coords.length - 1; i++) {
            this.midPoints.push({
                ...midpoint(coords[i], coords[i + 1]),
                id: v4(),
                properties: {
                    type: 'midpoint',
                    vertexId: i
                }
            });
        }

        this.source.setData({
            type: 'FeatureCollection',
            features: [this.feature, ...this.points, ...this.midPoints]
        });
    }

    override getEdited(): DrawingObjectPolygonGeometry {
        return {
            polygonCoordinates: [...this.feature!.geometry.coordinates[0].map((x) => [...x])]
        };
    }

    private setActive(vertexId: number, value: boolean) {
        this.source.updateData({
            update: [
                {
                    id: this.points[vertexId].id as string,
                    addOrUpdateProperties: [
                        {
                            key: 'active',
                            value
                        }
                    ]
                }
            ]
        });
    }

    private updateFeature(type: 'move' | 'pointDrag') {
        this.source.updateData({
            update: [
                {
                    id: this.feature!.id as string,
                    newGeometry: this.feature!.geometry
                }
            ]
        });

        // If we're dragging only one vertex
        if (type === 'pointDrag') {
            assert(this.dragVertexId != null);

            const leftMidPoint = this.dragVertexId - 1 < 0 ? this.midPoints.length - 1 : this.dragVertexId - 1;
            const midpoints = [this.midPoints[this.dragVertexId], this.midPoints[leftMidPoint]];

            this.source.updateData({
                update: [
                    {
                        id: this.points[this.dragVertexId].id as number,
                        newGeometry: this.points[this.dragVertexId].geometry
                    },
                    ...midpoints.map((x) => ({
                        id: x.id as string,
                        newGeometry: x.geometry
                    }))
                ]
            });
        } else {
            this.source.updateData({
                update: [
                    ...this.points.map((x) => ({
                        id: x.id as string,
                        newGeometry: x.geometry
                    })),
                    ...this.midPoints.map((x) => ({
                        id: x.id as string,
                        newGeometry: x.geometry
                    }))
                ]
            });
        }
    }

    private convertMidPointIntoVertex(index: number) {
        assert(this.feature);
        this.points = [
            ...this.points.slice(0, index + 1),
            // Take position from mid point
            turf.point([...this.midPoints[index].geometry.coordinates], { type: 'vertex', vertexId: 0, active: false }, { id: v4() }),
            ...this.points.slice(index + 1)
        ];

        // Update all vertexIds
        for (let i = 0; i < this.points.length; i++) {
            this.points[i].properties.vertexId = i;
        }

        const leftVertex = this.points[index];
        const newVertex = this.points[arrayWrap(index, 1, this.points)];
        const rightVertex = this.points[arrayWrap(index, 2, this.points)];

        this.midPoints = [
            ...this.midPoints.slice(0, index + 1),
            turf.point([...this.midPoints[index].geometry.coordinates], { type: 'midpoint', vertexId: 0 }, { id: v4() }),
            ...this.midPoints.slice(index + 1)
        ];

        this.midPoints[index].geometry = midpointGeoJson(newVertex, leftVertex).geometry;
        this.midPoints[arrayWrap(index, 1, this.midPoints)].geometry = midpointGeoJson(newVertex, rightVertex).geometry;
        for (let i = 0; i < this.midPoints.length; i++) {
            this.midPoints[i].properties.vertexId = i;
        }

        // Update the polygon
        this.feature.geometry = turf.polygon([
            [...this.points.map((x) => [...x.geometry.coordinates]), [...this.points[0].geometry.coordinates]]
        ]).geometry;

        this.source.updateData({
            add: [this.points[index + 1], this.midPoints[index + 1]],
            update: [
                {
                    id: this.feature.id as string,
                    newGeometry: this.feature.geometry
                },
                ...this.points
                    .filter((x) => x.properties.vertexId !== index + 1)
                    .map((x) => ({
                        id: x.id as number,
                        newGeometry: x.geometry,
                        addOrUpdateProperties: [
                            {
                                key: 'vertexId',
                                value: x.properties.vertexId
                            }
                        ]
                    })),
                ...this.midPoints
                    .filter((x) => x.properties.vertexId !== index + 1)
                    .map((x) => ({
                        id: x.id as string,
                        newGeometry: x.geometry,
                        addOrUpdateProperties: [
                            {
                                key: 'vertexId',
                                value: x.properties.vertexId
                            }
                        ]
                    }))
            ]
        });
    }
}

function midpointGeoJson(start: GeoJSON.Feature<GeoJSON.Point>, end: GeoJSON.Feature<GeoJSON.Point>): GeoJSON.Feature<GeoJSON.Point> {
    return {
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: [
                (start.geometry.coordinates[0] + end.geometry.coordinates[0]) / 2,
                (start.geometry.coordinates[1] + end.geometry.coordinates[1]) / 2
            ]
        },
        properties: {}
    };
}

function midpoint(start: number[], end: number[]): GeoJSON.Feature<GeoJSON.Point> {
    return {
        type: 'Feature',
        geometry: {
            type: 'Point',
            coordinates: [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2]
        },
        properties: {}
    };
}

function arrayWrap(i: number, diff: number, array: { length: number }) {
    const newIdx = i + (diff % array.length);
    if (newIdx >= array.length) {
        return newIdx - array.length;
    }

    if (newIdx < 0) {
        return array.length + newIdx;
    }

    return newIdx;
}
